import React, { useContext, useCallback } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Dismiss12Regular,
  LocationDismiss20Filled,
} from '@fluentui/react-icons';
import {
  DataGridBody,
  DataGridRow,
  DataGrid,
  DataGridCell,
  createTableColumn,
  makeStyles, tokens, Button,
  Text,
  mergeClasses
} from '@fluentui/react-components';
import { useBulkLocationsSearchContext } from './bulk-locations-search-provider';
import { TargetPinIcon } from './pin-icons';

const useStyles = makeStyles({
  row: {
    '&:hover .location-action-button': {
      visibility: 'visible',
    },
  },
  cell: {
    paddingRight: tokens.spacingHorizontalNone,
    paddingLeft: tokens.spacingHorizontalXS,
    justifyContent: 'space-between',
    alignItems: 'center',
    display: 'flex',
    minHeight: '34px',
    '&:focus-visible .location-action-button, &:focus-within .location-action-button': {
      visibility: 'visible',
    },
  },
  cellItem: {
    alignItems: 'center',
    display: 'flex',
  },
  locationActionButton: {
    visibility: 'hidden',
    marginLeft: tokens.spacingHorizontalSNudge,
  },
  targetIcon: {
    marginRight: '7px',
    marginLeft: '3px',
    flexShrink: '0',
  },
  excludeIcon: {
    marginRight: tokens.spacingHorizontalXS,
    color: tokens.colorStatusDangerForeground1,
    flexShrink: '0',
  },
});

const columns = [
  createTableColumn({
    columnId: 'target',
    renderCell: (item, classes, i18n, onUpdateLocation, onRemoveLocation) => (
      <React.Fragment>
        <span className={mergeClasses(classes.cellItem, item.target.isTargeted && 'target-location-row', !item.target.isTargeted && 'exclude-location-row')} >
          {item.target.isTargeted ? <TargetPinIcon className={classes.targetIcon} /> : <LocationDismiss20Filled className={classes.excludeIcon} />}
          <Text>{item.target.name}</Text>
          <Button
            className={mergeClasses(classes.locationActionButton, 'location-action-button')}
            size="small"
            onClick={() => onUpdateLocation(item)}
          >
            {item.target.isTargeted ? i18n.getString(_TL_('Exclude')) : i18n.getString(_TL_('Target'))}
          </Button>
        </span>
        <Button
          appearance="subtle"
          aria-label={i18n.getString(_TL_('Remove'))}
          icon={<Dismiss12Regular />}
          onClick={() => onRemoveLocation(item)}
        />
      </React.Fragment>),
  }),
];

function MatchedLocationsTable() {
  const i18n = useContext(I18nContext);
  const classes = useStyles();
  const { matchedLocations, setMatchedLocations, setSearchResults } = useBulkLocationsSearchContext();

  const onUpdateLocation = useCallback((item) => {
    const updatedLocations = matchedLocations.map(loc =>
      (loc.target.Location.Id === item.target.Location.Id
        ? { ...loc, target: { ...loc.target, isTargeted: !loc.target.isTargeted } }
        : loc));
    setMatchedLocations(updatedLocations);
  }, [matchedLocations, setMatchedLocations]);

  const onRemoveLocation = useCallback((item) => {
    const updatedMatched = matchedLocations.filter(location => location.target.name !== item.target.name);
    setMatchedLocations(updatedMatched);

    setSearchResults(prevResults => prevResults.filter(res =>
      res.Location && res.Location.Id !== item.target.Location.Id));
  }, [matchedLocations, setMatchedLocations, setSearchResults]);

  return (
    <DataGrid
      items={matchedLocations}
      columns={columns}
    >
      <DataGridBody>
        {({ item, rowId }) => (
          <DataGridRow
            className={classes.row}
            key={rowId}
          >
            {({ renderCell }) => (
              <DataGridCell className={mergeClasses(classes.cell, 'bulk-locations-dataGridCell')} focusMode="group">
                {renderCell(item, classes, i18n, onUpdateLocation, onRemoveLocation)}
              </DataGridCell>
            )}
          </DataGridRow>
        )}
      </DataGridBody>
    </DataGrid>
  );
}

export default MatchedLocationsTable;
